import {
  WSElement,
  WSText,
  WSTimeline,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import {
  Invoice,
  InvoicePayment,
  InvoicePaymentEvents,
  InvoicePaymentMethod,
  InvoicePaymentStatusReason
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import capitalize from "lodash/capitalize";
import { TextBulleted } from "../../../shared/components/TextBulleted";

type Props = {
  invoice: Invoice;
};

const statusReasonMap: Record<InvoicePaymentStatusReason, string> = {
  [InvoicePaymentStatusReason.INSUFFICIENT_FUNDS]: "Insufficient funds",
  [InvoicePaymentStatusReason.ACCOUNT_NOT_FOUND]: "Account not found",
  [InvoicePaymentStatusReason.UNAUTHORIZED]: "Unauthorized",
  [InvoicePaymentStatusReason.GENERIC_BANK_ERROR]: "Bank error",
  [InvoicePaymentStatusReason.GENERIC_CARD_ERROR]: "Card error",
  [InvoicePaymentStatusReason.CARD_CURRENCY_NOT_SUPPORTED]:
    "Card currency not supported",
  [InvoicePaymentStatusReason.CARD_EXPIRED]: "Card expired",
  [InvoicePaymentStatusReason.CARD_INPUT_INVALID]: "Invalid card input"
};

// `sourceMetadata.institution` is optional, so a failed card payment (or any
// record the institution never landed on) used to render no payment method at
// all. `method` is always present, so it identifies the method even then.
// Ported from wingspan-app@410846570 (WIN-15093), remodelled on v3's richer
// InvoicePaymentMethod enum.
const PAYMENT_METHOD_LABELS: Record<InvoicePaymentMethod, string> = {
  [InvoicePaymentMethod.ACH]: "Bank account",
  [InvoicePaymentMethod.CARD]: "Card",
  [InvoicePaymentMethod.CARD_PUSH]: "Card",
  [InvoicePaymentMethod.WIRE]: "Wire transfer",
  [InvoicePaymentMethod.RTP]: "Real-time payment",
  [InvoicePaymentMethod.FED_NOW]: "FedNow",
  [InvoicePaymentMethod.CHECK]: "Check",
  [InvoicePaymentMethod.BOOK]: "Wingspan wallet",
  [InvoicePaymentMethod.OFF_PLATFORM]: "Off-platform payment"
};

const getPaymentMethodLabel = (payment: InvoicePayment): string => {
  const institution = payment.sourceMetadata?.institution;

  return institution
    ? capitalize(institution)
    : (PAYMENT_METHOD_LABELS[payment.method] ?? "Payment method");
};

const getEventDetails = (event: keyof InvoicePaymentEvents, date: Date) => {
  switch (event) {
    case "initiatedAt":
      return `Payment initiated (${toWSDateString(date, "monthDayYear")})`;
    case "completedAt":
      return `Payment completed (${toWSDateString(date, "monthDayYear")})`;
    case "failedAt":
      return `Payment failed (${toWSDateString(date, "monthDayYear")})`;
    case "returnedAt":
      return `Payment returned (${toWSDateString(date, "monthDayYear")})`;
    case "cancelledAt":
      return `Payment cancelled (${toWSDateString(date, "monthDayYear")})`;
  }
};

export const InvoicePayments: React.FC<Props> = ({ invoice }) => {
  return (
    <WSTimeline
      items={(invoice.payments || []).map(payment => {
        const events = Object.entries(payment.events || {})
          .filter(([, date]) => !!date)
          .map(([key, date]) => ({
            event: key as keyof InvoicePaymentEvents,
            date: date as Date
          }));

        events.sort((a, b) => a.date.getTime() - b.date.getTime());

        return {
          date: events[0]?.date,
          pills: [
            payment.status,
            `${formatMoney(payment.amount ?? 0, invoice.currency || "USD")} payment`
          ].filter(Boolean) as string[],
          content: (
            <>
              <WSElement mb="M">
                <WSText.ParagraphSm color="gray400">
                  Payment method:{" "}
                </WSText.ParagraphSm>
                <WSText color="gray600">
                  {getPaymentMethodLabel(payment)}
                  {payment.sourceMetadata?.mask
                    ? ` (${payment.sourceMetadata.mask})`
                    : ""}
                </WSText>
              </WSElement>

              <TextBulleted color="gray600">
                {events.map(event => (
                  <li key={event.event}>
                    <WSText.ParagraphSm>
                      {getEventDetails(event.event, event.date)}
                    </WSText.ParagraphSm>
                    {(event.event === "failedAt" ||
                      event.event === "returnedAt") &&
                      payment.statusReason && (
                        <WSText.ParagraphSm color="gray600">
                          {statusReasonMap[payment.statusReason]}
                        </WSText.ParagraphSm>
                      )}
                  </li>
                ))}
              </TextBulleted>
            </>
          )
        };
      })}
    />
  );
};
